/**
 * Debounced, one-at-a-time saving for editors without a Save button.
 *
 * The status report editor saves itself as the PM works. Three rules keep
 * that safe:
 *
 *  - A save waits for a pause. It runs `delayMs` after the last edit, so a
 *    sentence costs one save rather than one per keystroke.
 *  - Saves never overlap. The report is written whole, so two saves racing
 *    could land out of order and the older one would roll back the newer.
 *  - A save only covers the edits that existed when it started. Anything
 *    changed while it was in flight is still unsaved afterwards.
 *
 * Framework-free so these rules can be tested without rendering the page.
 */

export type SaveStatus =
  | 'saved'    // nothing outstanding
  | 'pending'  // edited, waiting for a pause before saving
  | 'saving'
  | 'error'    // the last save failed; its edits are still unsaved

export interface SaveState {
  status: SaveStatus
  /** Why the last save failed. Only set with status 'error'. */
  error?: string
}

export interface Autosaver<A> {
  /** Record an edit and restart the wait for a pause. */
  edited(): void
  /** Save anything unsaved now, after the save already running if there is
   *  one. Resolves true once nothing is left unsaved, false if a save failed. */
  flush(): Promise<boolean>
  /** Save once now even when nothing is unsaved, passing `arg` through to the
   *  save function. Queues behind a running save like any other. */
  saveNow(arg: A): Promise<boolean>
  /** Re-run whatever failed last: saveNow with its argument, or otherwise the
   *  unsaved edits. For an explicit Retry only - flush() never re-issues a
   *  failed saveNow, so switching month can't quietly repeat a Submit. */
  retry(): Promise<boolean>
  /** Forget unsaved edits, for when the PM chose to leave without them. */
  discard(): void
  /** True while edits are unsaved or a save is queued or running. */
  busy(): boolean
}

export function createAutosaver<A>({ save, delayMs, onState }: {
  save: (arg?: A) => Promise<void>
  delayMs: number
  onState: (state: SaveState) => void
}): Autosaver<A> {
  // Every edit bumps `edits`. A successful save sets `saved` to the value
  // `edits` had when it started, so the two differ exactly while something
  // is unsaved.
  let edits = 0
  let saved = 0
  let queued = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let queue: Promise<unknown> = Promise.resolve()

  // Every save runs through this chain, so each starts only after the last
  // has finished. attempt() turns a failed save into an error state; the
  // catch is for anything else that throws, which would otherwise leave the
  // chain rejected and quietly stop every save after it.
  const enqueue = (task: () => Promise<boolean>): Promise<boolean> => {
    queued++
    const run = queue
      .then(task)
      .catch((e: unknown) => {
        console.error('Autosave task failed', e)
        try { onState({ status: 'error', error: 'Could not save.' }) } catch { /* reporting is what failed */ }
        return false
      })
      .finally(() => { queued-- })
    queue = run
    return run
  }

  // The argument of the save that failed last, for retry(). A failed
  // saveNow('submitted') must be retried as a submit: flush() alone would find
  // nothing unsaved, or save the content without the transition.
  let failedArg: A | undefined

  const attempt = async (arg?: A): Promise<boolean> => {
    const covers = edits
    onState({ status: 'saving' })
    try {
      await save(arg)
    } catch (e) {
      failedArg = arg
      onState({ status: 'error', error: e instanceof Error ? e.message : 'Could not save.' })
      return false
    }
    failedArg = undefined
    // Never backwards: discard() may have moved past this save meanwhile.
    saved = Math.max(saved, covers)
    onState({ status: edits === saved ? 'saved' : 'pending' })
    return true
  }

  const flush = (): Promise<boolean> => {
    clearTimeout(timer)
    return enqueue(async () => {
      // Unlike the timer, keep going until caught up: the caller is about to
      // act on the result, e.g. switch month.
      while (edits !== saved) {
        if (!(await attempt())) return false
      }
      return true
    })
  }

  const saveNow = (arg: A): Promise<boolean> => {
    clearTimeout(timer)
    return enqueue(() => attempt(arg))
  }

  return {
    edited() {
      edits++
      onState({ status: 'pending' })
      clearTimeout(timer)
      // One save per pause. If editing resumes while it runs, the next pause
      // brings the next save.
      timer = setTimeout(() => void enqueue(async () => (edits === saved ? true : attempt())), delayMs)
    },
    flush,
    saveNow,
    retry: () => (failedArg === undefined ? flush() : saveNow(failedArg)),
    discard() {
      clearTimeout(timer)
      saved = edits
      failedArg = undefined
      // Nothing is outstanding now, bar a save that is already running.
      onState({ status: queued > 0 ? 'saving' : 'saved' })
    },
    busy: () => edits !== saved || queued > 0,
  }
}
